<template>
  <v-form
    v-if="currentRepairFile"
    ref="formCreateRepairCenter"
    v-model="formFinalizationValid"
    @submit.prevent="submit"
  >
    <v-card class="parent-card rounded-xl" :border="false" :elevation="0">
      <v-row>
        <v-col class="d-flex justify-center pa-0 mb-10" cols="12">
          <img alt="check-svg" class="check-svg mr-4" src="@/assets/icons/file/check_round.svg" />
          <p class="title">
            {{
              $t('REPAIR_FILE_FINALIZATION.TITLE', {
                internal_command_number: currentRepairFile.internal_command_number
              })
            }}
          </p>
        </v-col>
        <v-col class="d-flex justify-center pa-0 mb-5" cols="12">
          <p class="sub-title">{{ $t('REPAIR_FILE_FINALIZATION.SUB_TITLE.TRACKING_NUMBER') }}</p>
        </v-col>
        <v-col class="pa-0 mb-1" cols="12">
          <v-text-field
            v-model="trackingNumber"
            :rules="checkTrackingNumberRequired ? requiredRules : []"
            type="text"
            required
          />
        </v-col>
        <v-col class="d-flex justify-center pa-0 mb-10" cols="12">
          {{ $t('REPAIR_FILE_FINALIZATION.SUB_TITLE.TRACKING_NUMBER_INFO') }}
        </v-col>

        <v-col class="d-flex justify-center pa-0 mb-5" cols="6" md="12">
          <p class="sub-title">{{ $t('REPAIR_FILE_FINALIZATION.SUB_TITLE.PRINT') }}</p>
        </v-col>
        <v-col class="d-flex justify-center pa-0 mb-10" cols="6" md="12">
          <v-btn
            :loading="loading"
            class="pa-5 boom-button"
            type="submit"
            variant="outlined"
            @click.stop.prevent="getFileAttachmentForRepairer()"
          >
            <p class="btn-text">{{ $t('REPAIR_FILE_FINALIZATION.BUTTON.REPAIRER_PRINT') }}</p>
            <template v-slot:prepend>
              <img alt="print-icon" class="print-svg" src="@/assets/icons/file/print.svg" />
            </template>
          </v-btn>
        </v-col>

        <v-row no-gutters class="card-box" cols="12">
          <v-card
            v-if="cardBox.packingSlip"
            link
            :disabled="loading"
            @click.stop.prevent="getPackingSlipPdf()"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.TRANSPORTER_ORDER') }}
            </p>
            <p class="text-grey">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.TRANSPORTER_ORDER_INFO') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.orderRepairWaitingTransportLabel"
            link
            :disabled="loading"
            @click.stop.prevent="getWaitingTransportRepairOrderPdf()"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER') }}
            </p>
            <p class="text-grey">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER_INFO') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.orderRepairWithTransportLabel"
            link
            :disabled="loading"
            @click.stop.prevent="getWithTransportRepairOrderPdf()"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER') }}
            </p>
            <p class="text-grey">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER_INFO') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.orderRepairWithoutTransportLabel"
            link
            :disabled="loading"
            @click.stop.prevent="getWithoutTransportRepairOrderPdf()"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER') }}
            </p>
            <p
              v-if="
                currentRepairFile.initial_transporter_type ===
                TransportTypeConstant.WITHOUT_TRANSPORTER
              "
              class="text-grey"
            >
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.REPAIR_ORDER_INFO') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.afterSaleService"
            link
            :disabled="loading"
            @click.stop.prevent="getRepairAfterSaleServicePdf()"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.AFTER_SALE_SERVICE') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.customerBilling"
            link
            :disabled="loading"
            @click.stop.prevent="downloadFiles(currentRepairFile.product_buy_proof_file_path_aws)"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.BILLING') }}
            </p>
          </v-card>
          <v-card
            v-if="cardBox.customerExtensionGuarantee"
            link
            :disabled="loading"
            @click.stop.prevent="
              downloadFiles(currentRepairFile.product_guarantee_extension_proof_file_path_aws)
            "
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.GUARANTEE') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.customerForceExtensionGuarantee"
            link
            :disabled="loading"
            @click.stop.prevent="
              downloadFiles(currentRepairFile.product_force_support_proof_file_path_aws)
            "
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.FORCE_GUARANTEE') }}
            </p>
          </v-card>

          <v-card
            v-if="cardBox.asbestosDeclaration"
            link
            :disabled="loading"
            @click.stop.prevent="
              downloadFiles(currentRepairFile.product_asbestos_proof_file_path_aws)
            "
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.ASBESTOS') }}
            </p>
          </v-card>
          <v-card
            v-if="
              cardBox.dangerLabel &&
              currentRepairFile?.packet_battery_watt_hour &&
              currentRepairFile?.packet_battery_watt_hour >= 100
            "
            link
            :disabled="loading"
            href="/danger-max-label.pdf"
            download="Etiquette_produit_dangereux.pdf"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.DANGER_LABEL') }}
            </p>
            <p class="text-grey">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.TRANSPORTER_ORDER_INFO') }}
            </p>
          </v-card>

          <v-card
            v-if="
              cardBox.dangerLabel &&
              currentRepairFile?.packet_battery_watt_hour &&
              currentRepairFile?.packet_battery_watt_hour < 100
            "
            link
            :disabled="loading"
            href="/danger-min-label.pdf"
            download="Etiquette_produit_dangereux.pdf"
            class="content-card rounded-xl flex-fill"
          >
            <img alt="dialog-icon" class="svg-check-rounded" src="@/assets/icons/file/file.svg" />
            <p class="text-decoration-underline">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.DANGER_LABEL') }}
            </p>
            <p class="text-grey">
              {{ $t('REPAIR_FILE_FINALIZATION.CARD.TRANSPORTER_ORDER_INFO') }}
            </p>
          </v-card>
        </v-row>

        <v-col class="d-flex justify-center pa-0 mb-5" cols="6" md="12">
          <p class="sub-title">{{ $t('REPAIR_FILE_FINALIZATION.SUB_TITLE.SHARE') }}</p>
        </v-col>

        <v-col class="d-flex justify-center pa-0" cols="12">
          <v-col cols="6" lg="3">
            <v-checkbox
              v-model="client"
              hide-details
              :label="$t('REPAIR_FILE_FINALIZATION.CHECKBOX.CLIENT_SHARE')"
            ></v-checkbox>
          </v-col>
          <v-col cols="6" lg="3" class="d-flex align-center">
            <div class="background-grey-block">
              <img
                alt="pen"
                class="pen-svg mr-2"
                src="@/assets/icons/file/pen.svg"
                @click="editClientEmail"
                v-if="!editingClient"
              />
              <span v-if="!editingClient">{{ editedClientEmail }}</span>
              <input
                v-else
                type="text"
                v-model="editedClientEmail"
                @blur="saveClientEmail"
                @keyup.enter="saveClientEmail"
              />
            </div>
          </v-col>
        </v-col>

        <v-col class="d-flex justify-center pa-0" cols="12">
          <v-col cols="6" lg="3">
            <v-checkbox
              v-model="preparer"
              hide-details
              :label="$t('REPAIR_FILE_FINALIZATION.CHECKBOX.PREPARER_SHARE')"
            ></v-checkbox>
          </v-col>
          <v-col cols="6" lg="3" class="d-flex align-center">
            <div class="background-grey-block">
              <img
                alt="pen"
                class="pen-svg mr-2"
                src="@/assets/icons/file/pen.svg"
                @click="editPreparerEmail"
                v-if="!editingPreparer"
              />
              <span v-if="!editingPreparer">{{ editedPreparerEmail }}</span>
              <input
                v-else
                type="text"
                v-model="editedPreparerEmail"
                @blur="savePreparerEmail"
                @keyup.enter="savePreparerEmail"
              />
            </div>
          </v-col>
        </v-col>

        <v-col class="d-flex justify-center pa-0" cols="12">
          <v-col cols="6" lg="3">
            <v-checkbox
              v-model="commercial"
              hide-details
              :label="$t('REPAIR_FILE_FINALIZATION.CHECKBOX.COMMERCIAL_SHARE')"
            ></v-checkbox>
          </v-col>
          <v-col cols="6" lg="3" class="d-flex align-center">
            <div class="background-grey-block">
              <img
                alt="pen"
                class="pen-svg mr-2"
                src="@/assets/icons/file/pen.svg"
                @click="editCommercialEmail"
                v-if="!editingCommercial"
              />
              <span v-if="!editingCommercial">{{ editedCommercialEmail }}</span>
              <input
                v-else
                type="text"
                v-model="editedCommercialEmail"
                @blur="saveCommercialEmail"
                @keyup.enter="saveCommercialEmail"
              />
            </div>
          </v-col>
        </v-col>

        <v-col class="d-flex justify-center pa-0 ga-4" cols="12">
          <v-btn
            v-if="typeIsCustomerService"
            :loading="loading"
            class="pl-15 pr-15 boom-button"
            variant="outlined"
            @click="redirectToDashboard()"
          >
            {{ $t('REPAIR_FILE_FINALIZATION.BUTTON.BACK') }}
          </v-btn>
          <v-btn
            :disabled="!formFinalizationValid"
            :loading="loading"
            class="pl-15 pr-15 boom-button"
            type="submit"
            variant="outlined"
          >
            {{ $t('REPAIR_FILE_FINALIZATION.BUTTON.VALIDATION') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-form>
</template>
<script>
import RepairFileService from '@/services/RepairFileService'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'
import TransportModalityConstant from '@/constants/transportModalityConstant'
import PdfService from '@/services/PdfService'
import { auth as $store } from '@/store/AuthModule'
import TransportTypeConstant from '@/constants/transportTypeConstant'
import {
  getEmailPattern,
  handleResponseError
} from '@/components/utils/globalFunction'
import ExpressistTransporterService from '@/services/transporters/ExpressistTransporterService'
import Validator from '@/components/utils/validator'

export default {
  name: 'CreateRepairFileFinalizationPage',
  computed: {
    TransportTypeConstant() {
      return TransportTypeConstant
    },
    checkTrackingNumberRequired() {
      return (
        this.currentRepairFile.initial_transporter_type ===
          TransportTypeConstant.IMMEDIATE_TRANSPORTER ||
        this.currentRepairFile.initial_transporter_type ===
          TransportTypeConstant.CUSTOMER_SERVICE_TRANSPORTER
      )
    },
    typeIsCustomerService() {
      return (
        this.currentRepairFile.initial_transporter_type ===
        TransportTypeConstant.CUSTOMER_SERVICE_TRANSPORTER
      )
    }
  },
  data() {
    return {
      mounted: false,
      loading: false,
      formFinalizationValid: false,
      currentRepairFile: null,
      trackingNumber: null,
      filesListPath: null,

      cardBox: {
        packingSlip: false,
        orderRepairWaitingTransportLabel: false,
        orderRepairWithoutTransportLabel: false,
        orderRepairWithTransportLabel: false,

        customerBilling: false,
        customerExtensionGuarantee: false,
        customerForceExtensionGuarantee: false,
        asbestosDeclaration: false,
        afterSaleService: false,
        dangerLabel: false
      },

      client: false,
      editingClient: false,
      editedClientEmail: '',

      preparer: false,
      editingPreparer: false,
      editedPreparerEmail: '',

      commercial: false,
      editingCommercial: false,
      editedCommercialEmail: '',
      requiredRules: Validator.requiredRules
    }
  },
  watch: {
    editedClientEmail() {
      // "mounted" is used to prevent the "editedClientEmail" watcher from setting the checkbox to true
      if (this.mounted) {
        this.client = getEmailPattern().test(this.editedClientEmail)
      }
      this.mounted = true
    },
    editedPreparerEmail() {
      this.preparer = getEmailPattern().test(this.editedPreparerEmail)
    },
    editedCommercialEmail() {
      this.commercial = getEmailPattern().test(this.editedCommercialEmail)
    }
  },
  async created() {
    this.loading = true
    if (this.$route.params.repairFileBoomerangId.length !== 0) {
      this.currentRepairFile = await RepairFileService.get(this.$route.params.repairFileBoomerangId)
      this.trackingNumber = this.currentRepairFile.tracking_number
      if (
        this.currentRepairFile.status_detail !==
          RepairFileStatusDetailConstant.FINALIZE_REPAIR_FILE &&
        this.currentRepairFile.status_detail !==
          RepairFileStatusDetailConstant.TRANSPORTER_REPAIR_FILE_TRANSFER
      ) {
        this.$router.push({ name: 'Dashboard' })
      }
      await this.patchShowCard()
      this.loading = false
    } else {
      this.$router.push({ name: 'Dashboard' })
    }
  },
  methods: {
    editClientEmail() {
      this.editingClient = true
      this.editedClientEmail = this.currentRepairFile.email
    },
    saveClientEmail() {
      this.currentRepairFile.email = this.editedClientEmail
      this.editingClient = false
    },
    editPreparerEmail() {
      this.editingPreparer = true
    },
    savePreparerEmail() {
      this.editingPreparer = false
    },
    editCommercialEmail() {
      this.editingCommercial = true
    },
    saveCommercialEmail() {
      this.editingCommercial = false
    },
    getCurrentCollaborator() {
      return $store.state.currentCollaborator
    },
    async submit() {
      this.loading = true
      const data = {
        tracking_number: this.trackingNumber,
        requestor_collaborator_id: this.getCurrentCollaborator().technical_id
      }

      if (this.client) {
        data['client'] = this.currentRepairFile.email
      }
      if (this.preparer) {
        data['preparer'] = this.editedPreparerEmail
      }
      if (this.commercial) {
        data['commercial'] = this.editedCommercialEmail
      }

      try {
        await RepairFileService.finalizationRepairFileUpdate(
          this.$route.params.repairFileBoomerangId,
          data
        )
        await this.$router.push({ name: 'Dashboard' })
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    patchShowCard() {
      if (
        this.currentRepairFile.initial_transporter_modality ===
          TransportModalityConstant.WORKSHOP ||
        this.currentRepairFile.initial_transporter_modality === TransportModalityConstant.STORE ||
        this.currentRepairFile.initial_transporter_modality ===
          TransportModalityConstant.EXCLUDING_SUPPORT ||
        this.currentRepairFile.initial_transporter_type ===
          TransportTypeConstant.WITHOUT_TRANSPORTER
      ) {
        this.cardBox.orderRepairWithoutTransportLabel = true
        this.cardBox.afterSaleService = true
      }
      if (
        this.currentRepairFile.initial_transporter_type ===
        TransportTypeConstant.CUSTOMER_SERVICE_TRANSPORTER
      ) {
        this.cardBox.orderRepairWaitingTransportLabel = true
        this.cardBox.afterSaleService = true
      }
      if (
        this.currentRepairFile.initial_transporter_type ===
        TransportTypeConstant.IMMEDIATE_TRANSPORTER
      ) {
        this.cardBox.orderRepairWithTransportLabel = true
        this.cardBox.afterSaleService = true
        if (this.currentRepairFile?.tracking_number) {
          this.cardBox.packingSlip = true
        }
      }
      if (this.currentRepairFile.product_asbestos_proof_file_path) {
        this.cardBox.asbestosDeclaration = true
      }
      if (this.currentRepairFile.product_buy_proof_file_path) {
        this.cardBox.customerBilling = true
      }
      if (this.currentRepairFile.product_guarantee_extension_proof_file_path) {
        this.cardBox.customerExtensionGuarantee = true
      }
      if (this.currentRepairFile.product_force_support_proof_file_path) {
        this.cardBox.customerForceExtensionGuarantee = true
      }
      if (this.currentRepairFile?.packet_battery_watt_hour) {
        this.cardBox.dangerLabel = true
      }
      this.editedClientEmail = this.currentRepairFile.email || ''
    },
    async getRepairAfterSaleServicePdf() {
      this.loading = true
      try {
        const response = await PdfService.getRepairPackagePdf(this.currentRepairFile.boomerang_id)
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          'fiche_sav_client_' + this.currentRepairFile.internal_command_number + '.pdf'
        )
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getWaitingTransportRepairOrderPdf() {
      try {
        this.loading = true
        const response = await PdfService.getRepairOrderWaitingTransportPdf(
          this.currentRepairFile.boomerang_id
        )
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          'ordre_de_réparation_' + this.currentRepairFile.internal_command_number + '.pdf'
        )
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getWithTransportRepairOrderPdf() {
      try {
        this.loading = true
        const response = await PdfService.getRepairOrderWithTransportPdf(
          this.currentRepairFile.boomerang_id
        )
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          'ordre_de_réparation_' + this.currentRepairFile.internal_command_number + '.pdf'
        )
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getWithoutTransportRepairOrderPdf() {
      try {
        this.loading = true
        const response = await PdfService.getRepairOrderWithoutTransportPdf(
          this.currentRepairFile.boomerang_id
        )
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          'ordre_de_réparation_' + this.currentRepairFile.internal_command_number + '.pdf'
        )
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getFileAttachmentForRepairer() {
      try {
        this.loading = true
        const response = await PdfService.getFileAttachmentForRepairer(
          this.currentRepairFile.boomerang_id
        )
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          'fichier_du_dossier_' + this.currentRepairFile.internal_command_number + '.pdf'
        )
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async getPackingSlipPdf() {
      try {
        this.loading = true
        const response = await ExpressistTransporterService.getPackingSlip(
          this.currentRepairFile.boomerang_id
        )
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        const filename =
          'etiquette_transport_' + this.currentRepairFile.internal_command_number + '.pdf'
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async downloadFiles(file_url) {
      if (file_url) {
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = file_url
        document.body.appendChild(a)
        a.click()
        await new Promise((resolve) => setTimeout(resolve, 500))
        document.body.removeChild(a)
      }
    },
    redirectToDashboard() {
      this.$router.push('/dashboard')
    }
  }
}
</script>

<style scoped>
.v-text-field {
  margin-left: 30%;
  margin-right: 30%;
}

.parent-card {
  display: flex;
  padding: 40px !important;
  margin: 40px !important;
}

.title {
  font-size: 32px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.check-svg {
  filter: invert(49%) sepia(39%) saturate(3419%) hue-rotate(60deg) brightness(95%) contrast(79%);
  width: 40px;
  height: 40px;
}

.pen-svg {
  width: 15px;
  height: 15px;
}

.pen-svg:hover {
  cursor: pointer;
}

.sub-title {
  color: #559a1b;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.print-svg {
  width: 14px;
  height: 14px;
}

.btn-text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.content-card {
  display: flex;
  max-height: 150px;
  max-width: 150px;
  gap: 10px;
  padding: 20px 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 20px;
  border: 1px solid #dadada;
  background: #fff;
  box-shadow: 6px 6px 12px 0 rgba(218, 218, 218, 0.4);
}

.card-box {
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
  gap: 20px;
}

.v-checkbox {
  width: fit-content;
}

.background-grey-block {
  font-weight: 400;
  line-height: normal;
  padding: 10px 20px;
  background-color: #efefef;
  border-radius: 20px;
  width: fit-content;
}
</style>
